import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { URLSearchParams } from "url";

import { Tab } from "@pentabd/ui";

import { ErrorModal, SuccessModal } from "@components";

import { Employee as InventoryEmployee } from "@containers/Inventory/EmployeeDetails/components/Employee";

import { getAccess } from "@helpers/permissionController";
import { useUser } from "@hooks/useUser";
import GeneralInfo from "./GeneralInfo";
import SpecPreparation from "./SpecPreparation";
import TenderNotice from "./TenderNotice";
import TenderOpening from "./TenderOpening";
import TenderEvolution from "./TenderEvolution";
import NOA from "./NOA";
import FinalReceiving from "./FinalReceiving";
import Agreement from "./Agreement";

interface IDType {
  id: number | null;
}

export const Procurement = ({ id }: IDType) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams]: [URLSearchParams, Function] =
    useSearchParams();
  const changeActiveTab = setActiveTab;

  const closeErrorModal = () => setIsErrorModalOpen(false);
  const closeSuccessModal = () => setIsSuccessModalOpen(false);

  const tabData = [
    {
      label: t("PROCUREMENT_CREATE.TABS.GENERAL_INFO"),
      component: (
        <GeneralInfo
          id={id}
          setSearchParams={setSearchParams}
          changeActiveTab={changeActiveTab}
          // setIsErrorModalOpen={setIsErrorModalOpen}
          // setIsSuccessModalOpen={setIsSuccessModalOpen}
        />
      ),
    },
    {
      label: t("PROCUREMENT_CREATE.TABS.SPEC_PREPARATION"),
      component: id ? (
        <SpecPreparation
          id={id}
          setSearchParams={setSearchParams}
          changeActiveTab={changeActiveTab}
          setIsErrorModalOpen={setIsErrorModalOpen}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
        />
      ) : (
        <></>
      ),
    },
    {
      label: t("PROCUREMENT_CREATE.TABS.TENDER_NOTICE_PUBLICATION"),
      component: id ? (
        <TenderNotice
          id={id}
          changeActiveTab={changeActiveTab}
          setIsErrorModalOpen={setIsErrorModalOpen}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
        />
      ) : (
        <></>
      ),
    },
    {
      label: t("PROCUREMENT_CREATE.TABS.TENDER_OPERATING"),
      component: id ? (
        <TenderOpening
          id={id}
          setSearchParams={setSearchParams}
          changeActiveTab={changeActiveTab}
          setIsErrorModalOpen={setIsErrorModalOpen}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
        />
      ) : (
        <></>
      ),
    },
    {
      label: t("PROCUREMENT_CREATE.TABS.TENDER_EVOLUTION"),
      component: id ? (
        <TenderEvolution
          id={id}
          setSearchParams={setSearchParams}
          changeActiveTab={changeActiveTab}
          setIsErrorModalOpen={setIsErrorModalOpen}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
        />
      ) : (
        <></>
      ),
    },
    {
      label: t("PROCUREMENT_CREATE.TABS.NOA_ISSUE_DATE"),
      component: id ? (
        <NOA
          id={id}
          changeActiveTab={changeActiveTab}
          setIsErrorModalOpen={setIsErrorModalOpen}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
        />
      ) : (
        <></>
      ),
    },
    {
      label: t("PROCUREMENT_CREATE.TABS.AGREEMENT"),
      component: id ? (
        <Agreement
          id={id}
          changeActiveTab={changeActiveTab}
          setIsErrorModalOpen={setIsErrorModalOpen}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
        />
      ) : (
        <></>
      ),
    },
    {
      label: t("PROCUREMENT_CREATE.TABS.FINAL_RECEIVING"),
      component: id ? (
        <FinalReceiving
          id={id}
          setSearchParams={setSearchParams}
          changeActiveTab={changeActiveTab}
          setIsErrorModalOpen={setIsErrorModalOpen}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
        />
      ) : (
        <></>
      ),
    },
  ];

  useEffect(() => {
    setActiveTab((prev) => (prev = Number(searchParams.get("tab"))));
  }, [searchParams]);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
    };
    return reset();
  }, []);

  return (
    <>
      <SuccessModal
        isModalOpen={isSuccessModalOpen}
        action={closeSuccessModal}
      />
      <ErrorModal isModalOpen={isErrorModalOpen} closeModal={closeErrorModal} />
      <Tab active={activeTab} setActiveTab={changeActiveTab}>
        <Tab.Heads>
          {tabData.map(({ label }, i) => (
            <Tab.Item
              key={i}
              index={i}
              label={label}
              disabled={i > 0 && !id ? true : false}
            />
          ))}
        </Tab.Heads>
        <Tab.ContentWrapper>
          {tabData.map(({ component }, i) => (
            <Tab.Content key={i} index={i}>
              {component}
            </Tab.Content>
          ))}
        </Tab.ContentWrapper>
      </Tab>
    </>
  );
};
